// localPrinterSettings.ts - نسخة محدثة مع كل إعدادات الباركود والفاتورة

// ================== أنواع الإعدادات ==================

// إعدادات طابعة الفواتير
export interface InvoicePrinterSettings {
  printer_type: 'windows' | 'usb' | 'ip';
  printer_name: string;
  printer_ip: string;
  printer_port: number;
  printer_usb_vid: string;
  printer_usb_pid: string;
  paper_width: number; // بالملم
  font_size: number;
  align: 'right' | 'center' | 'left';
  show_logo: boolean;
  show_phone: boolean;
  show_address: boolean;
  show_footer: boolean;
  company_name: string;
  company_phone: string;
  company_address: string;
  
  // ✅ إعدادات حجم اللوجو (جديدة)
  logo_width: number;      // عرض اللوجو بالبوصة
  logo_height: number;     // ارتفاع اللوجو بالبوصة
  logo_align: 'left' | 'center' | 'right';  // محاذاة اللوجو
}

// إعدادات طابعة الباركود
export interface BarcodePrinterSettings {
  printer_type: 'windows' | 'usb' | 'ip';
  printer_name: string;
  printer_ip: string;
  printer_port: number;
  printer_usb_vid: string;
  printer_usb_pid: string;
  
  // نوع الباركود
  barcode_type: 'code128' | 'code39' | 'ean13' | 'ean8' | 'upc' | 'qr';
  
  // ✅ إعدادات الباركود (كلها منقولة من الكود)
  barcode_width: number;      // عرض الباركود (بوصة)
  barcode_height: number;      // ارتفاع الباركود (بوصة)
  barcode_align: 'left' | 'center' | 'right';  // محاذاة الباركود
  barcode_text_size: number;   // حجم نص الباركود (px)
  barcode_show_text: boolean;  // إظهار رقم الباركود كنص
  
  // ✅ إعدادات النص
  label_font_size: number;     // حجم الخط الأساسي للملصق
  label_show_name: boolean;    // إظهار اسم المنتج
  label_show_price: boolean;   // إظهار السعر
  label_name_position: 'above' | 'below';  // مكان اسم المنتج
  label_price_position: 'above' | 'below'; // مكان السعر
  label_name_align: 'left' | 'center' | 'right';  // محاذاة الاسم
  label_price_align: 'left' | 'center' | 'right'; // محاذاة السعر
  
  // ✅ إعدادات الورق والهوامش
  paper_type: 'single' | 'precut';  // نوع الورق
  label_width: number;        // عرض الملصق (بوصة)
  label_height: number;       // ارتفاع الملصق (بوصة)
  columns_count: number;      // عدد الأعمدة (للورق المقسم)
  rows_count: number;         // عدد الصفوف (للورق المقسم)
  gap_horizontal: number;     // فراغ أفقي بين الملصقات (بوصة)
  gap_vertical: number;       // فراغ عمودي بين الملصقات (بوصة)
  start_column: number;       // عمود البداية
  start_row: number;          // صف البداية
  margin_top: number;         // هامش علوي (بوصة)
  margin_bottom: number;      // هامش سفلي (بوصة)
  margin_right: number;       // هامش أيمن (بوصة)
  margin_left: number;        // هامش أيسر (بوصة)
}

// إعدادات طابعة المطبخ
export interface KitchenPrinterSettings {
  enabled: boolean;
  printer_type: 'windows' | 'usb' | 'ip';
  printer_name: string;
  printer_ip: string;
  printer_port: number;
  printer_usb_vid: string;
  printer_usb_pid: string;
  paper_width: number; // بالملم
  auto_print: boolean;
  sound_enabled: boolean;
  columns: number;
  show_timers: boolean;
}

// إعدادات درج الكاشير
export interface DrawerSettings {
  enabled: boolean;
  connection_type: 'printer_windows' | 'serial' | 'printer_ip' | 'printer_usb';
  port: string;
  printer_name: string;
  printer_ip: string;
  printer_port: number;
  printer_usb_vid: string;
  printer_usb_pid: string;
  open_command: string;
  command_format: 'text' | 'hex';
  auto_open: boolean;
  shortcut_key: string;
}

// إعدادات الضريبة والخدمة (إعدادات عامة - مش على مستوى الجهاز)
export interface TaxSettings {
  tax_rate: number;
  service_rate: number;
  apply_tax_to_pos: boolean;
  apply_service_to_pos: boolean;
  apply_tax_to_tables: boolean;
  apply_service_to_tables: boolean;
}

// جميع إعدادات الجهاز المحلي
export interface LocalDeviceSettings {
  deviceId: string;
  deviceName: string;
  lastUpdated: string;
  
  invoice_printer: InvoicePrinterSettings;
  barcode_printer: BarcodePrinterSettings;
  kitchen_printer: KitchenPrinterSettings;
  drawer: DrawerSettings;
  tax: TaxSettings;  // ملاحظة: دي إعدادات عامة، لكن هنخزنها محلياً كـ fallback
}

// ================== الإعدادات الافتراضية ==================

const DEFAULT_INVOICE_PRINTER: InvoicePrinterSettings = {
  printer_type: 'windows',
  printer_name: 'Microsoft Print to PDF',
  printer_ip: '',
  printer_port: 9100,
  printer_usb_vid: '',
  printer_usb_pid: '',
  paper_width: 80,
  font_size: 12,
  align: 'right',
  show_logo: true,
  show_phone: true,
  show_address: true,
  show_footer: true,
  company_name: '',
  company_phone: '',
  company_address: '',
  
  // ✅ إعدادات اللوجو الافتراضية
  logo_width: 1.5,      // 1.5 بوصة عرض
  logo_height: 0.8,     // 0.8 بوصة ارتفاع
  logo_align: 'center'  // محاذاة وسط
};

const DEFAULT_BARCODE_PRINTER: BarcodePrinterSettings = {
  printer_type: 'windows',
  printer_name: 'Microsoft Print to PDF',
  printer_ip: '',
  printer_port: 9100,
  printer_usb_vid: '',
  printer_usb_pid: '',
  barcode_type: 'code128',
  
  // ✅ إعدادات الباركود (القيم الحالية من الكود)
  barcode_width: 1.5,
  barcode_height: 0.8,
  barcode_align: 'center',
  barcode_text_size: 8,      // حجم نص الباركود (صغير)
  barcode_show_text: true,   // إظهار رقم الباركود
  
  // ✅ إعدادات النص (القيم الحالية)
  label_font_size: 10,
  label_show_name: true,
  label_show_price: true,
  label_name_position: 'below',
  label_price_position: 'below',
  label_name_align: 'center',
  label_price_align: 'center',
  
  // ✅ إعدادات الورق والهوامش (القيم الحالية)
  paper_type: 'single',
  label_width: 1.5,
  label_height: 1.0,
  columns_count: 2,
  rows_count: 10,
  gap_horizontal: 0.1,
  gap_vertical: 0.1,
  start_column: 1,
  start_row: 1,
  margin_top: 0.1,
  margin_bottom: 0.1,
  margin_right: 0.1,
  margin_left: 0.1,
};

const DEFAULT_KITCHEN_PRINTER: KitchenPrinterSettings = {
  enabled: false,
  printer_type: 'windows',
  printer_name: '',
  printer_ip: '',
  printer_port: 9100,
  printer_usb_vid: '',
  printer_usb_pid: '',
  paper_width: 80,
  auto_print: true,
  sound_enabled: true,
  columns: 2,
  show_timers: true
};

const DEFAULT_DRAWER: DrawerSettings = {
  enabled: false,
  connection_type: 'printer_windows',
  port: '',
  printer_name: '',
  printer_ip: '',
  printer_port: 9100,
  printer_usb_vid: '',
  printer_usb_pid: '',
  open_command: 'xprinter',
  command_format: 'text',
  auto_open: true,
  shortcut_key: 'F8'
};

const DEFAULT_TAX: TaxSettings = {
  tax_rate: 14,
  service_rate: 12,
  apply_tax_to_pos: true,
  apply_service_to_pos: true,
  apply_tax_to_tables: true,
  apply_service_to_tables: true
};

export const DEFAULT_DEVICE_SETTINGS: LocalDeviceSettings = {
  deviceId: generateDeviceId(),
  deviceName: 'جهازي',
  lastUpdated: new Date().toISOString(),
  invoice_printer: { ...DEFAULT_INVOICE_PRINTER },
  barcode_printer: { ...DEFAULT_BARCODE_PRINTER },
  kitchen_printer: { ...DEFAULT_KITCHEN_PRINTER },
  drawer: { ...DEFAULT_DRAWER },
  tax: { ...DEFAULT_TAX }
};

// ================== دوال مساعدة ==================

function generateDeviceId(): string {
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    deviceId = 'device_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('device_id', deviceId);
  }
  return deviceId;
}

const STORAGE_KEY = 'local_device_settings';

// ================== دوال التخزين المحلي ==================

/**
 * تحميل جميع إعدادات الجهاز من localStorage
 */
export function loadLocalDeviceSettings(): LocalDeviceSettings {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // دمج مع الإعدادات الافتراضية (لأي حقول جديدة)
      return {
        ...DEFAULT_DEVICE_SETTINGS,
        ...parsed,
        invoice_printer: { ...DEFAULT_INVOICE_PRINTER, ...parsed.invoice_printer },
        barcode_printer: { ...DEFAULT_BARCODE_PRINTER, ...parsed.barcode_printer },
        kitchen_printer: { ...DEFAULT_KITCHEN_PRINTER, ...parsed.kitchen_printer },
        drawer: { ...DEFAULT_DRAWER, ...parsed.drawer },
        tax: { ...DEFAULT_TAX, ...parsed.tax }
      };
    }
  } catch (error) {
    console.error('خطأ في تحميل الإعدادات المحلية:', error);
  }
  
  // إنشاء إعدادات جديدة
  const newSettings = { ...DEFAULT_DEVICE_SETTINGS, deviceId: generateDeviceId() };
  saveLocalDeviceSettings(newSettings);
  return newSettings;
}

/**
 * حفظ جميع إعدادات الجهاز في localStorage
 */
export function saveLocalDeviceSettings(settings: LocalDeviceSettings): void {
  try {
    settings.lastUpdated = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    
    // إرسال حدث للتحديث
    window.dispatchEvent(new CustomEvent('device-settings-changed', { 
      detail: { settings } 
    }));
  } catch (error) {
    console.error('خطأ في حفظ الإعدادات المحلية:', error);
  }
}

/**
 * تحديث جزء معين من الإعدادات
 */
export function updateInvoicePrinterSettings(updates: Partial<InvoicePrinterSettings>): void {
  const settings = loadLocalDeviceSettings();
  settings.invoice_printer = { ...settings.invoice_printer, ...updates };
  saveLocalDeviceSettings(settings);
}

export function updateBarcodePrinterSettings(updates: Partial<BarcodePrinterSettings>): void {
  const settings = loadLocalDeviceSettings();
  settings.barcode_printer = { ...settings.barcode_printer, ...updates };
  saveLocalDeviceSettings(settings);
}

export function updateKitchenPrinterSettings(updates: Partial<KitchenPrinterSettings>): void {
  const settings = loadLocalDeviceSettings();
  settings.kitchen_printer = { ...settings.kitchen_printer, ...updates };
  saveLocalDeviceSettings(settings);
}

export function updateDrawerSettings(updates: Partial<DrawerSettings>): void {
  const settings = loadLocalDeviceSettings();
  settings.drawer = { ...settings.drawer, ...updates };
  saveLocalDeviceSettings(settings);
}

export function updateTaxSettings(updates: Partial<TaxSettings>): void {
  const settings = loadLocalDeviceSettings();
  settings.tax = { ...settings.tax, ...updates };
  saveLocalDeviceSettings(settings);
}

/**
 * جلب إعدادات محددة (للتوافق مع الكود القديم)
 */
export function getDrawerSettings(): DrawerSettings {
  return loadLocalDeviceSettings().drawer;
}

export function getInvoicePrinterSettings(): InvoicePrinterSettings {
  return loadLocalDeviceSettings().invoice_printer;
}

export function getBarcodePrinterSettings(): BarcodePrinterSettings {
  return loadLocalDeviceSettings().barcode_printer;
}

export function getKitchenPrinterSettings(): KitchenPrinterSettings {
  return loadLocalDeviceSettings().kitchen_printer;
}

export function getTaxSettings(): TaxSettings {
  return loadLocalDeviceSettings().tax;
}

/**
 * التحقق من تفعيل درج الكاشير
 */
export function isDrawerEnabled(): boolean {
  return loadLocalDeviceSettings().drawer.enabled;
}

/**
 * الحصول على اختصار الدرج
 */
export function getDrawerShortcut(): string {
  return loadLocalDeviceSettings().drawer.shortcut_key;
}

/**
 * تصدير الإعدادات كـ JSON (للنسخ الاحتياطي)
 */
export function exportSettings(): string {
  const settings = loadLocalDeviceSettings();
  return JSON.stringify(settings, null, 2);
}

/**
 * استيراد الإعدادات من JSON
 */
export function importSettings(jsonString: string): boolean {
  try {
    const settings = JSON.parse(jsonString);
    // التحقق من البنية الأساسية
    if (!settings.deviceId || !settings.invoice_printer) {
      throw new Error('ملف إعدادات غير صالح');
    }
    saveLocalDeviceSettings(settings);
    return true;
  } catch (error) {
    console.error('خطأ في استيراد الإعدادات:', error);
    return false;
  }
}